import axios from "axios";
import BASE_URL, { API_ENDPOINTS } from "../constants/config";
import { getToken, getRefreshToken, clearStoredAuth, getSessionEpoch } from "./authStorage";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create axios instance (baseURL includes /api/v1 for versioned API)
const axiosInstance = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  headers: {
    "Content-Type": "application/json",
    "X-Platform": "web",
  },
});

// Request interceptor - attach token; for FormData, do not set Content-Type so browser sends multipart with boundary
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = false; // let browser set multipart with boundary
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - 401 → single refresh, queue parallel failures, retry all with new token
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (!originalRequest || originalRequest._skipAuthRefresh) {
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      originalRequest.headers = originalRequest.headers || {};

      // 401 from a request that started under a previous session (user logged
      // out / switched account mid-flight): don't refresh, don't retry — that
      // would mix two users' data.
      const epochAtRequest = getSessionEpoch();

      if (isRefreshing) {
        return new Promise((outerResolve, outerReject) => {
          failedQueue.push({
            resolve: (token) => {
              if (getSessionEpoch() !== epochAtRequest) {
                outerReject(error);
                return;
              }
              originalRequest.headers.Authorization = `Bearer ${token}`;
              outerResolve(axiosInstance(originalRequest));
            },
            reject: (err) => outerReject(err),
          });
        });
      }

      isRefreshing = true;

      try {
        const storedRefreshToken = getRefreshToken();
        if (!storedRefreshToken) throw new Error("No refresh token found");

        const refreshResponse = await axiosInstance.post(
          API_ENDPOINTS.authRefreshToken,
          { refreshToken: storedRefreshToken },
          { _skipAuthRefresh: true, headers: { "Content-Type": "application/json" } }
        );
        const newToken = refreshResponse.data.token;
        const newRefreshToken = refreshResponse.data.refreshToken;

        if (!newToken || !newRefreshToken) throw new Error("No new tokens received");

        // A logout/login happened while this refresh was in flight — these
        // tokens belong to the old session. Storing them would overwrite the
        // new user's credentials.
        if (getSessionEpoch() !== epochAtRequest) {
          processQueue(new Error("Session changed during refresh"), null);
          return Promise.reject(error);
        }

        const hadLocal = !!localStorage.getItem("token");
        if (hadLocal) localStorage.setItem("token", newToken);
        else sessionStorage.setItem("token", newToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Refresh token failed:", {
          message: refreshError.response?.data?.message || refreshError.message,
          status: refreshError.response?.status,
        });

        processQueue(refreshError, null);

        // Refresh may have failed because the session legitimately changed
        // mid-flight; only force-logout if this session is still the live one.
        if (getSessionEpoch() === epochAtRequest) {
          clearStoredAuth();
          alert("Session expired. Please login again.");
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
